/**
 * Layout Preview
 * 
 * Renders a read-only preview of the requirement portal as the executor would see it.
 * Shows the form layout with disabled fields so the admin can visualize the result.
 */

import React from 'react';
import {
	Box,
	ColumnLayout,
	Container,
	FormField,
	Header,
	Input,
	SpaceBetween,
	Table,
	Wizard,
} from '@amzn/awsui-components-console';

import { AvailableField, PortalLayout, LayoutSection } from './layoutSchema.types';

interface LayoutPreviewProps {
	layout: PortalLayout;
	availableFields: AvailableField[];
	workflowName: string;
	workflowIntention?: string;
}

function PreviewSection({ section, availableFields }: { section: LayoutSection; availableFields: AvailableField[] }) {
	const renderPreviewField = (field: typeof section.fields[number]) => {
		const meta = availableFields.find(f => f.attributeId === field.attributeId);

		if (meta?.reviewOnly) return null;
		const label = field.label || meta?.attributeName || field.attributeId;
		const dataType = meta?.dataType || 'STRING';

		return (
			<FormField
				key={field.attributeId}
				label={label}
				description={meta?.description}
			>
				<Input
					value=""
					disabled
					placeholder={`${dataType} input`}
					onChange={() => {}}
				/>
			</FormField>
		);
	};

	const chunks: { type: 'grid' | 'full'; fields: typeof section.fields }[] = [];
	for (const field of section.fields) {
		if (field.fullWidth) {
			chunks.push({ type: 'full', fields: [field] });
		} else {
			const lastChunk = chunks[chunks.length - 1];
			if (lastChunk && lastChunk.type === 'grid') {
				lastChunk.fields.push(field);
			} else {
				chunks.push({ type: 'grid', fields: [field] });
			}
		}
	}

	return (
		<Container header={<Header>{section.title}</Header>}>
			<SpaceBetween size="l">
				{chunks.map((chunk, idx) =>
					chunk.type === 'grid' ? (
						<ColumnLayout key={idx} columns={section.columns} variant="default">
							{chunk.fields.map(renderPreviewField)}
						</ColumnLayout>
					) : (
						chunk.fields.map(renderPreviewField)
					)
				)}
				{section.fields.length === 0 && (
					<Box textAlign="center" color="text-body-secondary" padding="l">
						No fields in this section
					</Box>
				)}
			</SpaceBetween>
		</Container>
	);
}

export default function LayoutPreview({ layout, availableFields, workflowName, workflowIntention }: LayoutPreviewProps) {
	const [activeStepIndex, setActiveStepIndex] = React.useState(0);

	const placedFieldIds = new Set<string>();
	layout.steps.forEach(step =>
		step.sections.forEach(section =>
			section.fields.forEach(field => placedFieldIds.add(field.attributeId))
		)
	);
	const unplacedFields = availableFields.filter(f => !placedFieldIds.has(f.attributeId) && !f.reviewOnly);

	const steps = layout.steps.map(step => ({
		title: step.title,
		description: step.description,
		content: (
			<SpaceBetween size="l">
				{step.sections.map(section => (
					<PreviewSection
						key={section.id}
						section={section}
						availableFields={availableFields}
					/>
				))}
				{step.sections.length === 0 && (
					<Box textAlign="center" color="text-body-secondary" padding="xl">
						This step has no sections yet
					</Box>
				)}
			</SpaceBetween>
		),
	}));

	if (unplacedFields.length > 0) {
		steps.push({
			title: 'Additional Fields',
			description: 'These fields were not included in the custom layout but are required by the workflow.',
			content: (
				<Container header={<Header>Remaining Fields</Header>}>
					<SpaceBetween size="l">
						{unplacedFields.map(field => (
							<FormField
								key={field.attributeId}
								label={field.attributeName}
								description={field.description}
							>
								<Input
									value=""
									disabled
									placeholder={`${field.dataType || 'STRING'} input`}
									onChange={() => {}}
								/>
							</FormField>
						))}
					</SpaceBetween>
				</Container>
			),
		});
	}

	const isUpdateWorkflow = workflowIntention === 'UPDATE';

	steps.push({
		title: isUpdateWorkflow ? `Review Changes for ${workflowName}` : `Review Information for ${workflowName}`,
		description: undefined,
		content: (
			<SpaceBetween size="l">
				{layout.steps.map(step =>
					step.sections.map(section => {
						const sectionFields = section.fields;
						if (sectionFields.length === 0) return null;

						if (isUpdateWorkflow) {
							const diffItems = sectionFields.map(field => {
								const meta = availableFields.find(f => f.attributeId === field.attributeId);
								return {
									attributeId: field.attributeId,
									attributeName: field.label || meta?.attributeName || field.attributeId,
									previousValue: '(existing value)',
									newValue: '(new value)',
									hasChanged: true,
								};
							});

							return (
								<Container key={section.id} header={<Header>{section.title}</Header>}>
									<Table
										columnDefinitions={[
											{ id: 'name', header: 'Field', cell: (item) => item.attributeName },
											{ id: 'previous', header: 'Existing', cell: (item) => (
												<Box color="text-status-error">{item.previousValue}</Box>
											)},
											{ id: 'new', header: 'New', cell: (item) => (
												<Box color="text-status-success">{item.newValue}</Box>
											)},
										]}
										items={diffItems}
										variant="embedded"
										stripedRows
									/>
								</Container>
							);
						}

						const gridFields = sectionFields.filter(f => !f.fullWidth);
						const fullWidthFields = sectionFields.filter(f => f.fullWidth);

						const renderReviewField = (field: typeof sectionFields[number]) => {
							const meta = availableFields.find(f => f.attributeId === field.attributeId);
							return (
								<div key={field.attributeId}>
									<Box variant="awsui-key-label">{field.label || meta?.attributeName || field.attributeId}</Box>
									<Box color="text-body-secondary">—</Box>
								</div>
							);
						};

						return (
							<Container key={section.id} header={<Header>{section.title}</Header>}>
								<SpaceBetween size="l">
									{gridFields.length > 0 && (
										<ColumnLayout columns={section.columns} variant="text-grid" borders="horizontal">
											{gridFields.map(renderReviewField)}
										</ColumnLayout>
									)}
									{fullWidthFields.length > 0 && (
										<ColumnLayout columns={1} variant="text-grid" borders="horizontal">
											{fullWidthFields.map(renderReviewField)}
										</ColumnLayout>
									)}
								</SpaceBetween>
							</Container>
						);
					})
				)}
				{unplacedFields.length > 0 && (
					isUpdateWorkflow ? (
						<Container header={<Header>Additional Fields</Header>}>
							<Table
								columnDefinitions={[
									{ id: 'name', header: 'Field', cell: (item) => item.attributeName },
									{ id: 'previous', header: 'Existing', cell: (item) => (
										<Box color="text-status-error">{item.previousValue}</Box>
									)},
									{ id: 'new', header: 'New', cell: (item) => (
										<Box color="text-status-success">{item.newValue}</Box>
									)},
								]}
								items={unplacedFields.map(field => ({
									attributeId: field.attributeId,
									attributeName: field.attributeName,
									previousValue: '(existing value)',
									newValue: '(new value)',
								}))}
								variant="embedded"
								stripedRows
							/>
						</Container>
					) : (
						<Container header={<Header>Additional Fields</Header>}>
							<ColumnLayout columns={2} variant="text-grid" borders="horizontal">
								{unplacedFields.map(field => (
									<div key={field.attributeId}>
										<Box variant="awsui-key-label">{field.attributeName}</Box>
										<Box color="text-body-secondary">—</Box>
									</div>
								))}
							</ColumnLayout>
						</Container>
					)
				)}
				{layout.steps.every(step => step.sections.every(s => s.fields.length === 0)) && unplacedFields.length === 0 && (
					<Box textAlign="center" color="text-body-secondary" padding="xl">
						No fields placed yet
					</Box>
				)}
			</SpaceBetween>
		),
	});

	return (
		<Box padding="l">
			<SpaceBetween size="l">
				<Header variant="h2" description="This is how the executor will see the requirement portal">
					Portal Preview
				</Header>
				<Wizard
					i18nStrings={{
						stepNumberLabel: (n) => `Step ${n}`,
						collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
						skipToButtonLabel: (step) => `Skip to ${step.title}`,
						navigationAriaLabel: 'Steps',
						cancelButton: 'Cancel',
						previousButton: 'Previous',
						nextButton: 'Next',
						submitButton: 'Start Workflow',
						optional: 'optional',
					}}
					steps={steps}
					activeStepIndex={activeStepIndex}
					onNavigate={({ detail }) => setActiveStepIndex(detail.requestedStepIndex)}
					onSubmit={() => setActiveStepIndex(0)}
					onCancel={() => setActiveStepIndex(0)}
				/>
			</SpaceBetween>
		</Box>
	);
}
